import React from 'react';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RefreshCw, Loader2, Eye, EyeOff } from 'lucide-react';
import { getSubscriptionColumns } from './subscriptions/components/SubscriptionTableColumns';
import { SubscriptionStatusSelector } from './subscriptions/components/SubscriptionStatusSelector';
import { SubscriptionDetailsPanel } from './subscriptions/components/SubscriptionDetailsPanel';
import { useSubscriptionsAdmin } from './subscriptions/hooks/useSubscriptionsAdmin';
import { DisableHooksProvider } from '@/contexts/DisableHooksContext';
import { UpcomingPaymentsPanel } from './subscriptions/UpcomingPaymentsPanel';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { SubscriptionCleanupPanel } from './subscriptions/components/SubscriptionCleanupPanel';

export default function SubscriptionsPage() {
  const {
    subscriptions,
    loading,
    error,
    statusFilter,
    selectedSubscription,
    showDetailsDialog,
    actionLoading,
    isEditMode,
    subscriptionsCount,
    showUpcomingPayments,
    fetchData,
    handleStatusChange,
    handleClearFilter,
    handleViewSubscription,
    handleCloseDialog,
    handleStatusUpdate,
    handleDeleteSubscription,
    handleSearch,
    handleEditSubscription,
    handleCloseEditMode,
    toggleUpcomingPayments
  } = useSubscriptionsAdmin();

  const columns = getSubscriptionColumns({
    handleViewSubscription,
    handleStatusUpdate,
    handleDeleteSubscription
  });

  return (
    // Используем DisableHooksProvider для отключения хука useCourierRentals
    <DisableHooksProvider disableCourierHooks={true}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Управление подписками</h1>
            {subscriptionsCount !== null && (
              <p className="text-muted-foreground">
                Всего записей в базе данных: {subscriptionsCount}
              </p>
            )}
          </div>
          <div className="flex gap-2 items-center">
            {/* Добавляем переключатель для панели предстоящих платежей */}
            <div className="flex items-center space-x-2 mr-4">
              <Switch
                id="payment-panel-toggle"
                checked={showUpcomingPayments}
                onCheckedChange={toggleUpcomingPayments}
              />
              <Label htmlFor="payment-panel-toggle" className="text-sm">
                {showUpcomingPayments ? (
                  <span className="flex items-center gap-1">
                    <Eye className="h-4 w-4" />
                    Платежи
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <EyeOff className="h-4 w-4" />
                    Платежи
                  </span>
                )}
              </Label>
            </div>
            
            <SubscriptionStatusSelector 
              value={statusFilter}
              onChange={handleStatusChange}
              onClear={handleClearFilter}
              showFilterBar={false}
            />
            
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                console.log("Обновляем список подписок вручную");
                fetchData();
              }}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Улучшенная структура сетки для более гибкого размещения панелей на разных устройствах */}
        <div className={`grid grid-cols-1 ${showUpcomingPayments ? 'lg:grid-cols-12' : ''} gap-6`}>
          {/* На больших экранах: таблица подписок слева, панель с платежами справа */}
          <div className={`${showUpcomingPayments ? 'lg:col-span-9' : ''} lg:order-1 order-2`}>
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {subscriptionsCount === 0 && (
              <Alert>
                <AlertDescription>В базе данных нет подписок</AlertDescription>
              </Alert>
            )}
            
            {statusFilter && (
              <div className="flex flex-wrap gap-2 mb-4">
                <SubscriptionStatusSelector 
                  value={statusFilter}
                  onChange={handleStatusChange}
                  onClear={handleClearFilter}
                  showFilterBar={true}
                />
              </div>
            )}
            
            <DataTable
              data={subscriptions}
              columns={columns}
              searchable
              searchPlaceholder="Поиск по номеру аренды или имени курьера..."
              loading={loading}
              onRowClick={(subscription) => {
                handleViewSubscription(subscription.id);
              }}
              onSearch={handleSearch}
            />
          </div>
          
          {/* Панель с предстоящими платежами - только показываем когда showUpcomingPayments = true */}
          {showUpcomingPayments && (
            <div className="lg:col-span-3 lg:order-2 order-1">
              <UpcomingPaymentsPanel />
            </div>
          )}
        </div>

        {/* Панель очистки подписок */}
        <SubscriptionCleanupPanel onCleanupComplete={fetchData} />
        
        {selectedSubscription && (
          <SubscriptionDetailsPanel
            subscriptionId={selectedSubscription}
            open={showDetailsDialog}
            onOpenChange={handleCloseDialog}
            onStatusChange={handleStatusUpdate}
            onDelete={handleDeleteSubscription}
            onEdit={handleEditSubscription}
            isEditMode={isEditMode}
            onCloseEditMode={handleCloseEditMode}
            loading={actionLoading}
          />
        )}
      </div>
    </DisableHooksProvider>
  );
}
